import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { Loader2 } from 'lucide-react';

import { toaster } from '@/shared/libs';
import { Button, Dialog, DialogFooter } from '@/shared/ui';
import {
  getOfferDisplayName,
  useSetOfferStatusMutation,
} from '@entities/Offer';
import type { Offer } from '@psp-aggregator/sdk';

export interface PauseOfferModalProps {
  offer: Offer;
  onClose: () => void;
}

export const PauseOfferModal: FC<PauseOfferModalProps> = ({
  offer,
  onClose,
}) => {
  const { t } = useTranslation();
  const setStatus = useSetOfferStatusMutation();
  const isPaused = (offer.status ?? 'active') === 'paused';

  const handleConfirm = async (): Promise<void> => {
    try {
      await setStatus.mutateAsync({
        offerId: offer.id,
        status: isPaused ? 'active' : 'paused',
      });
      toaster.success({
        title: isPaused ? t('toast.offerResumed') : t('toast.offerPaused'),
      });
      onClose();
    } catch (error) {
      toaster.error({
        title: isAxiosError(error)
          ? (error.response?.data?.message ?? t('errors.unknown'))
          : t('errors.unknown'),
      });
    }
  };

  return (
    <Dialog
      title={
        isPaused
          ? t('pages.admin.offers.resume.title', {
              name: getOfferDisplayName(offer),
            })
          : t('pages.admin.offers.pause.title', {
              name: getOfferDisplayName(offer),
            })
      }
      width={480}
      onClose={onClose}>
      <p className="text-sm text-muted-foreground">
        {isPaused
          ? t('pages.admin.offers.resume.description')
          : t('pages.admin.offers.pause.description')}
      </p>
      <DialogFooter
        endSlot={
          <>
            <Button type="button" variant="secondary" onClick={onClose}>
              {t('buttons.cancel')}
            </Button>
            <Button
              disabled={setStatus.isPending}
              type="button"
              variant="brand"
              onClick={() => {
                void handleConfirm();
              }}>
              {setStatus.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : isPaused ? (
                t('pages.admin.offers.resume.confirm')
              ) : (
                t('pages.admin.offers.pause.confirm')
              )}
            </Button>
          </>
        }
      />
    </Dialog>
  );
};

PauseOfferModal.displayName = 'PauseOfferModal';
